import {Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isCpaCenterV2StepComplete} from 'src/CpaCenterList/components/cpaList.utils';
import {TAX_FILING_STATUS} from 'src/constants/constants';
import Box from 'src/DesignSystem/Box/Box';
import DSButton from 'src/DesignSystem/Button/Button';
import useNotify from 'src/DesignSystem/Notify/useNotify';
import PopUp from 'src/DesignSystem/PopUp/PopUp';
import {NotificationType} from 'src/store/app/app.reducer';
import {fetchDocumentsForGivenYear} from 'src/store/documents/documents.actions';
import {fetchQueries} from 'src/store/queries/queries.actions';
import {QueryStatus} from 'src/store/queries/queries.reducer';
import {selectQueriesForCurrReturn} from 'src/store/queries/queries.selector';
import {fetchTransactions} from 'src/store/transactions/transactions.actions';
import useCpaCenterV2Step from '../hooks/useCpaCenterV2Step';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useLayout from '../hooks/useLayout';
import UserQueriesSection from './components/UserQueriesSection';
import {getFilteredQueries} from './UserQueries.utils';
import TaxProfileSingleSection from '../TaxProfileSections/common/TaxProfileSummaryFilter/TaxProfileSingleSection';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {useSearchParams} from 'react-router-dom';
import {USER_QUERIES_URL_SEARCH_PARAMS} from './UserQueries.types';
import {ReduxStateType} from 'src/store/store';
import {TaxReturnType} from 'src/store/taxReturns/taxReturns.reducer';

const UserQueries = () => {
  const {activeYear} = useActiveYear();
  const dispatch = useDispatch();
  const {userId} = useCurrentUserId();
  const {notify} = useNotify();
  const {cpaFacingRetStatus, markStepComplete, isLoading} =
    useCpaCenterV2Step();
  const [isConfirmPopup, setIsConfirmPopup] = useState(false);
  const [searchParams] = useSearchParams();

  const currentReturnId = searchParams.get(
    USER_QUERIES_URL_SEARCH_PARAMS.RETURN_ID,
  )
    ? Number(searchParams.get(USER_QUERIES_URL_SEARCH_PARAMS.RETURN_ID))
    : null;
  const currentReturnType = searchParams.get(
    USER_QUERIES_URL_SEARCH_PARAMS.RETURN_TYPE,
  ) as TaxReturnType;

  const {queries} = useSelector((state: ReduxStateType) =>
    selectQueriesForCurrReturn(
      state,
      currentReturnId == null || currentReturnType == null
        ? null
        : {
            return_id: currentReturnId,
            return_type: currentReturnType,
          },
    ),
  );

  const relevantQueries = getFilteredQueries(queries);

  const isAnyUnresolvedQuery = relevantQueries.some(
    (query) => query.queryStatus !== QueryStatus.RESOLVED,
  );

  const onClosePopup = () => {
    setIsConfirmPopup(false);
  };

  const completeUserQueries = async () => {
    await markStepComplete(TAX_FILING_STATUS.PREP_STARTED);
    onClosePopup();
    notify(
      'Succesfully completed user queries step',
      NotificationType.success,
    );
  };

  const onClickNext = () => {
    if (isAnyUnresolvedQuery) {
      setIsConfirmPopup(true);
      return;
    }
    completeUserQueries();
  };

  const isEnabled =
    isCpaCenterV2StepComplete(
      cpaFacingRetStatus,
      TAX_FILING_STATUS.USER_QUERIES_SENT,
    ) &&
    !isCpaCenterV2StepComplete(
      cpaFacingRetStatus,
      TAX_FILING_STATUS.PREP_STARTED,
    ) &&
    !isLoading;

  useEffect(() => {
    dispatch(fetchQueries({userId, year: activeYear}));
    dispatch(fetchDocumentsForGivenYear(userId, activeYear));
    dispatch(fetchTransactions(userId, activeYear));
  }, [activeYear]);

  const {spaceLeftBelowTaxFilingSteps, DOCUMENT_BOTTOM_BAR_HEIGHT} =
    useLayout();

  return (
    <div>
      <div style={{display: 'flex'}}>
        <div style={{width: '70%'}}>
          {/* User Queries */}
          <UserQueriesSection
            returnId={currentReturnId}
            returnType={currentReturnType}
          />
        </div>
        <div
          style={{
            width: '30%',
          }}>
          {/* Tax Profile Summary */}
          <Box
            style={{
              height:
                spaceLeftBelowTaxFilingSteps - DOCUMENT_BOTTOM_BAR_HEIGHT,
              overflowY: 'hidden',
            }}>
            <TaxProfileSingleSection />
          </Box>
          <div>
            {isCpaCenterV2StepComplete(
              cpaFacingRetStatus,
              TAX_FILING_STATUS.PREP_STARTED,
            ) ? null : (
              <div
                style={{
                  display: 'flex',
                  height: DOCUMENT_BOTTOM_BAR_HEIGHT,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <DSButton
                  type="primary"
                  text="Start preparing the Return"
                  onClick={onClickNext}
                  disabled={!isEnabled}
                  style={{paddingInline: 80, paddingBlock: 10}}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <PopUp
        style={{width: 600}}
        isOpen={isConfirmPopup}
        primaryButtonTitle="Confirm"
        primaryButtonOnClick={completeUserQueries}
        primaryButtonDisabled={isLoading}
        secondaryButtonTitle={'Close'}
        secondaryButtonOnClick={onClosePopup}
        secondaryButtonDisabled={isLoading}
        onClose={onClosePopup}>
        <Typography style={{fontSize: 20, fontWeight: 600, marginBottom: 12}}>
          Are you sure you want to continue?
        </Typography>
        <Typography style={{marginBottom: 40}}>
          There are still some <strong>unresolved</strong> queries!!
        </Typography>
      </PopUp>
    </div>
  );
};

export default UserQueries;
